// n8n Configuration for Voice-to-Task App
// This file contains all n8n webhook endpoints and workflow settings

export interface N8nConfig {
  baseUrl: string;
  webhooks: {
    voiceUpload: string;
    taskCreate: string;
    taskUpdate: string;
    taskDelete: string;
    taskMove: string;
    taskList: string;
  };
  sse: {
    tasksStream: string;
  };
  workflows: {
    voiceTranscription: string;
    taskManagement: string;
    realTimeSync: string;
  };
}

// Development configuration (now using Express backend instead of n8n)
// Updated backend URL to use deployed Vercel backend instead of localhost
const developmentConfig: N8nConfig = {
  baseUrl: process.env.REACT_APP_BACKEND_URL || 'https://voice-task-backend.vercel.app', // Express backend URL
  webhooks: {
    // Voice processing endpoints
    voiceUpload: '/api/v1/voice/upload',

    // Task management endpoints
    taskCreate: '/api/v1/tasks',
    taskUpdate: '/api/v1/tasks',
    taskDelete: '/api/v1/tasks',
    taskMove: '/api/v1/tasks',
    taskList: '/api/v1/tasks',
  },
  sse: {
    // WebSocket endpoint (will be implemented later)
    tasksStream: '/ws',
  },
  workflows: {
    // n8n workflow IDs (will be populated after creating workflows)
    voiceTranscription: 'voice-transcription-workflow',
    taskManagement: 'task-management-workflow',
    realTimeSync: 'real-time-sync-workflow',
  },
};

// Production configuration (for VPS deployment)
const productionConfig: N8nConfig = {
  baseUrl: process.env.REACT_APP_BACKEND_URL || 'https://voice-task-backend.vercel.app',
  webhooks: {
    // Voice processing endpoints
    voiceUpload: '/api/v1/voice/upload',

    // Task management endpoints
    taskCreate: '/api/v1/tasks',
    taskUpdate: '/api/v1/tasks',
    taskDelete: '/api/v1/tasks',
    taskMove: '/api/v1/tasks',
    taskList: '/api/v1/tasks',
  },
  sse: {
    // WebSocket endpoint (will be implemented later)
    tasksStream: '/ws',
  },
  workflows: {
    // n8n workflow IDs (will be populated after creating workflows)
    voiceTranscription: 'voice-transcription-workflow',
    taskManagement: 'task-management-workflow',
    realTimeSync: 'real-time-sync-workflow',
  },
};

// Mock mode for testing without n8n backend
export const MOCK_MODE = process.env.REACT_APP_MOCK_MODE === 'true';

// Export the appropriate configuration based on environment
export const n8nConfig: N8nConfig =
  process.env.NODE_ENV === 'production' ? productionConfig : developmentConfig;

// Helper function to build full webhook URLs
export const getWebhookUrl = (endpoint: keyof N8nConfig['webhooks']): string => {
  return `${n8nConfig.baseUrl}${n8nConfig.webhooks[endpoint]}`;
};

// Helper function to build SSE URLs
export const getSSEUrl = (endpoint: keyof N8nConfig['sse']): string => {
  return `${n8nConfig.baseUrl}${n8nConfig.sse[endpoint]}`;
};

// Voice recording configuration
export const voiceConfig = {
  // Audio recording settings
  audio: {
    mimeType: 'audio/webm;codecs=opus', // Preferred format for web
    fallbackMimeType: 'audio/wav', // Fallback for older browsers
    sampleRate: 16000, // 16kHz is optimal for speech recognition
    channels: 1, // Mono audio
  },
  
  // Recording constraints
  recording: {
    maxDuration: 300, // 5 minutes max recording
    minDuration: 1, // 1 second minimum
    silenceThreshold: 0.01, // Silence detection threshold
    silenceTimeout: 3000, // Auto-stop after 3 seconds of silence
  },
  
  // File upload settings
  upload: {
    maxFileSize: 10 * 1024 * 1024, // 10MB max file size
    chunkSize: 1024 * 1024, // 1MB chunks for large files
    timeout: 30000, // 30 second timeout
  },
};

// Eisenhower Matrix configuration
export const matrixConfig = {
  quadrants: {
    'urgent-important': {
      id: 'urgent-important',
      title: 'Urgent & Important',
      subtitle: 'Do First',
      color: '#f44336', // Red
      priority: 1,
    },
    'important': {
      id: 'important',
      title: 'Important, Not Urgent',
      subtitle: 'Schedule',
      color: '#ff9800', // Orange
      priority: 2,
    },
    'urgent': {
      id: 'urgent',
      title: 'Urgent, Not Important',
      subtitle: 'Delegate',
      color: '#2196f3', // Blue
      priority: 3,
    },
    'neither': {
      id: 'neither',
      title: 'Neither Urgent nor Important',
      subtitle: 'Eliminate',
      color: '#4caf50', // Green
      priority: 4,
    },
  },
  
  // Default categorization keywords
  keywords: {
    urgent: ['urgent', 'asap', 'immediately', 'emergency', 'critical', 'deadline'],
    important: ['important', 'significant', 'crucial', 'essential', 'priority', 'goal'],
    routine: ['routine', 'regular', 'daily', 'weekly', 'maintenance'],
    eliminate: ['maybe', 'someday', 'if time', 'nice to have', 'optional'],
  },
};

// API response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface VoiceUploadResponse {
  transcriptionId: string;
  status: 'processing' | 'completed' | 'failed';
  transcription?: string;
  extractedTasks?: Task[];
}

export interface Task {
  id: string;
  title: string;
  description?: string;
  link?: string;
  quadrant: keyof typeof matrixConfig.quadrants;
  status: 'pending' | 'in-progress' | 'completed';
  voiceNoteUrl?: string;
  voice_note_url?: string; // Backend field
  transcription?: string;
  archived: boolean;
  createdAt: string;
  created_at?: string; // Backend field
  updatedAt: string;
  updated_at?: string; // Backend field
}

// SSE Event types
export interface SSEEvent {
  type: 'task-created' | 'task-updated' | 'task-deleted' | 'task-moved' | 'task-completed' | 'task-archived' | 'task-unarchived' | 'transcription-complete';
  data: any;
  timestamp: string;
}

// Mock data for testing
export const mockTasks: Task[] = [
  {
    id: 'mock-1',
    title: 'Complete project proposal',
    description: 'Finish the quarterly project proposal for the new client',
    quadrant: 'urgent-important',
    status: 'in-progress',
    archived: false,
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T14:30:00Z',
  },
  {
    id: 'mock-2',
    title: 'Schedule team meeting',
    description: 'Plan next week\'s sprint planning session',
    quadrant: 'important',
    status: 'pending',
    archived: false,
    createdAt: '2024-01-15T09:00:00Z',
    updatedAt: '2024-01-15T09:00:00Z',
  },
  {
    id: 'mock-3',
    title: 'Reply to emails',
    description: 'Respond to pending client emails',
    quadrant: 'urgent',
    status: 'pending',
    archived: false,
    createdAt: '2024-01-15T08:00:00Z',
    updatedAt: '2024-01-15T08:00:00Z',
  },
  {
    id: 'mock-4',
    title: 'Organize desk',
    description: 'Clean and organize workspace',
    quadrant: 'neither',
    status: 'pending',
    archived: false,
    createdAt: '2024-01-14T16:00:00Z',
    updatedAt: '2024-01-14T16:00:00Z',
  },
];

export default n8nConfig;
